import type { Locale } from '@infiltre/shared';
import { useUiStore } from '@/shared/hooks/use-shallow-store';
import { useDialog } from '@/shared/lib/dialog/use-dialog';
import { useT } from '@/shared/lib/i18n';
import { FLAG_BY_LOCALE } from './icons/flags';
import { PhaseTag } from './PhaseTag';
import { PolaroidFrame } from './PolaroidFrame';

export type LanguageCtaProps = {
  /**
   * Override the displayed locale. When omitted, the CTA reads the
   * current global locale from the zustand store.
   */
  value?: Locale;
  /**
   * Override the click handler. When omitted, the CTA opens the global
   * `language-picker` dialog (which writes back to the store).
   */
  onClick?: () => void;
  /** Tile size. `sm` ≈ 96px wide, `md` ≈ 128px. Default `md`. */
  size?: 'sm' | 'md';
  className?: string;
};

const SIZE_CLASS: Record<NonNullable<LanguageCtaProps['size']>, string> = {
  sm: 'w-[96px]',
  md: 'w-[128px]',
};

const FLAG_PX: Record<NonNullable<LanguageCtaProps['size']>, number> = {
  sm: 60,
  md: 80,
};

/**
 * Polaroid-stamped language CTA: flag image + `LANGUE` eyebrow + locale
 * code caption. Tap opens the shared language picker sheet (or a
 * caller-provided handler).
 */
export const LanguageCta = ({ value, onClick, size = 'md', className }: LanguageCtaProps) => {
  const t = useT();
  const storeLocale = useUiStore((s) => s.locale);
  const dialog = useDialog();
  const locale = value ?? storeLocale;
  const Flag = FLAG_BY_LOCALE[locale];
  const handleClick = onClick ?? (() => void dialog.open('language-picker', {}));

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`${t('settings.section.language')} — ${t(`locale.${locale}`)}`}
      className={[
        'group inline-flex items-center justify-center transition-transform duration-150 ease-out hover:-rotate-1 hover:scale-[1.03]',
        SIZE_CLASS[size],
        className ?? '',
      ].join(' ')}
      style={{ filter: 'drop-shadow(var(--shadow-stamp-sm))' }}
    >
      <PolaroidFrame index={0} waxPin color="var(--color-paper)">
        <div className="flex flex-col items-center gap-1.5 px-2 pt-2.5 pb-1.5">
          <PhaseTag size={size === 'sm' ? 9 : 10} color="rgba(0,0,0,.55)">
            {t('settings.section.language')}
          </PhaseTag>
          <Flag size={FLAG_PX[size]} />
          <div className="hand text-[20px] text-ink leading-none">{locale.toUpperCase()}</div>
        </div>
      </PolaroidFrame>
    </button>
  );
};
